import { observer } from 'mobx-react-lite';
import React from 'react';
import { I18nContext } from 'react-i18next';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import {
  AuthError,
  AuthLoading,
  ConfirmDialog,
  ErrorBoundary,
  Navigation,
  ScrollToTop,
  WorkspaceLoading,
} from './components/shared';
import ToasterProvider from './components/ToasterProvider';
import TokenSwitcher from './components/TokenSwitcher';
import { useAppInitialization } from './hooks/shared';
import i18n from './i18n';
import Billing from './pages/Billing';
import Calendar from './pages/Calendar';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Team from './pages/Team';
import { StoreContext, rootStore } from './stores';
import { confirmDialogStore } from './stores/ConfirmDialogStore';

const AppContent = observer(() => {
  // Use new app initialization logic
  const { isAuthenticating, isAuthenticated, isInitializing, isInitialized } =
    useAppInitialization();

  // Show authentication loading
  if (isAuthenticating) {
    return <AuthLoading />;
  }

  // Show authentication error
  if (!isAuthenticating && !isAuthenticated) {
    return <AuthError />;
  }

  // Show workspace loading
  if (isInitializing) {
    return <WorkspaceLoading />;
  }

  // Show app initialization loading
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing application...</p>
        </div>
      </div>
    );
  }

  // Show main app content
  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen pb-16">
        <Routes>
          {/* Workspace routes with /w/ prefix */}
          <Route path="/w/:workspaceSlug" element={<Calendar />} />
          <Route
            path="/w/:workspaceSlug/calendar/:view/:date"
            element={<Calendar />}
          />
          <Route
            path="/w/:workspaceSlug/training/:trainingId"
            element={<Calendar />}
          />
          <Route path="/w/:workspaceSlug/team" element={<Team />} />
          <Route path="/w/:workspaceSlug/team/:userId" element={<Profile />} />
          <Route path="/w/:workspaceSlug/profile" element={<Profile />} />
          <Route path="/w/:workspaceSlug/billing" element={<Billing />} />
          <Route path="/w/:workspaceSlug/settings" element={<Settings />} />

          {/* 404 route */}
          <Route path="/404" element={<NotFound />} />

          {/* Catch-all route for unmatched paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Navigation />
        <TokenSwitcher /> {/* TODO: Remove (debug feature) */}
        {/* Global confirmation dialog */}
        <ConfirmDialog
          isOpen={confirmDialogStore.isOpen}
          onClose={() => confirmDialogStore.handleCancel()}
          onConfirm={() => confirmDialogStore.handleConfirm()}
          config={confirmDialogStore.config}
        />
      </div>
    </div>
  );
});

const App: React.FC = observer(() => {
  return (
    <I18nContext.Provider value={{ i18n }}>
      <ErrorBoundary>
        <StoreContext.Provider value={rootStore}>
          <ToasterProvider>
            <Router>
              <ScrollToTop />
              <AppContent />
            </Router>
          </ToasterProvider>
        </StoreContext.Provider>
      </ErrorBoundary>
    </I18nContext.Provider>
  );
});

export default App;
